import { useEffect, useMemo, useState } from "react";
import type { GenerationPreview } from "../types.js";
import { PromptSourceBlocks } from "./prompt-source-blocks.js";

export type PromptPreviewState = "idle" | "loading" | "ready" | "error";

export function GenerationPromptPreview({
  preview,
  state,
  error,
  updatedAt,
  mode = "client_template",
}: {
  preview: GenerationPreview | null;
  state: PromptPreviewState;
  error: string;
  updatedAt: number | null;
  mode?: "client_template" | "free_prompt";
}) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const assetSummary = useMemo(
    () => (preview ? summarizeAssets(preview) : "—"),
    [preview],
  );

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(timer);
  }, [copied]);

  async function copyPrompt() {
    if (!preview?.renderedPrompt) return;
    await navigator.clipboard.writeText(preview.renderedPrompt);
    setCopied(true);
  }

  const status =
    state === "loading"
      ? "Обновляем…"
      : state === "ready" && updatedAt
        ? "Обновлено только что"
        : "";

  return (
    <section className="generation-prompt-preview" aria-live="polite">
      <header>
        <div>
          <span className="preview-title-row">
            <h2>Предпросмотр итоговой инструкции</h2>
            {status && <small className={state}>{status}</small>}
          </span>
          <p>
            {mode === "free_prompt"
              ? "Собирается только из вашего запроса, параметров площадки и загруженных референсов."
              : "Собирается из шаблона, данных клиента, фирменного стиля, выбранных материалов и ваших инструкций."}
          </p>
        </div>
        <div className="preview-actions">
          <button
            type="button"
            className="preview-action"
            disabled={!preview?.renderedPrompt}
            onClick={() => void copyPrompt()}
          >
            <span aria-hidden="true">▣</span>
            {copied ? "Скопировано" : "Скопировать"}
          </button>
          <button
            type="button"
            className="preview-action"
            disabled={!preview?.renderedPrompt}
            onClick={() => setExpanded(true)}
          >
            <span aria-hidden="true">↗</span>
            Развернуть
          </button>
        </div>
      </header>

      {state === "idle" ? (
        <div className="prompt-preview-empty">
          {mode === "free_prompt"
            ? "Опишите изображение и выберите площадку, чтобы увидеть итоговую инструкцию."
            : "Выберите клиента и шаблон, чтобы увидеть итоговую инструкцию."}
        </div>
      ) : state === "error" ? (
        <div className="prompt-preview-error">
          <strong>Не удалось обновить предпросмотр.</strong>
          <span>{error}</span>
        </div>
      ) : preview ? (
        <>
          <div className="prompt-preview-summary">
            {preview.mode === "free_prompt" ? (
              <>
                <Summary label="Источник данных" value="Только ваш ввод" />
                <Summary
                  label="Площадка"
                  value={`${preview.platform.name} · ${preview.platform.width} × ${preview.platform.height} px`}
                />
                <Summary
                  label="Референсы"
                  value={`${preview.references?.length ?? 0} ${imageCountLabel(preview.references?.length ?? 0)}`}
                />
                <Summary
                  label="Качество"
                  value={qualityLabel(preview.quality)}
                />
                <Summary
                  label="Формат"
                  value={preview.outputFormat.toUpperCase()}
                />
                <Summary
                  label="Генерация"
                  value={`${preview.count} ${imageCountLabel(preview.count)}`}
                />
              </>
            ) : (
              <>
                <Summary label="Режим" value="По шаблону клиента" />
                <Summary label="Клиент" value={preview.client?.name ?? "—"} />
                <Summary
                  label="Шаблон"
                  value={
                    preview.template
                      ? `${preview.template.name} · v${preview.template.versionNumber}`
                      : "—"
                  }
                />
                <Summary
                  label="Источник"
                  value={
                    preview.template?.category === "article"
                      ? "Текст статьи"
                      : "Пользовательский бриф"
                  }
                />
                <Summary
                  label="Площадка"
                  value={`${preview.platform.width} × ${preview.platform.height} px · ${ratio(preview.platform.width, preview.platform.height)} · ${preview.outputFormat.toUpperCase()}`}
                />
                <Summary
                  label="Качество"
                  value={qualityLabel(preview.quality)}
                />
                <Summary label="Материалы бренда" value={assetSummary} />
                <Summary
                  label="Генерация"
                  value={`${preview.count} ${imageCountLabel(preview.count)}`}
                />
              </>
            )}
          </div>

          {!preview.validation.valid && (
            <div className="prompt-preview-validation">
              Заполните обязательные параметры шаблона.
            </div>
          )}
          {preview.warnings.length > 0 && (
            <ul className="prompt-preview-warnings">
              {preview.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}
          <div className="prompt-preview-text">
            <PromptSourceBlocks
              prompt={preview.renderedPrompt}
              blocks={preview.promptBlocks}
            />
          </div>
        </>
      ) : (
        <div className="prompt-preview-loading">Собираем инструкцию…</div>
      )}

      {expanded && preview && (
        <div
          className="prompt-preview-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setExpanded(false);
          }}
        >
          <section
            className="prompt-preview-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="expanded-prompt-title"
          >
            <header>
              <div>
                <h2 id="expanded-prompt-title">Итоговая инструкция</h2>
                <p>Текст, который будет сохранён для будущей генерации.</p>
              </div>
              <button
                type="button"
                className="icon-button"
                aria-label="Закрыть предпросмотр"
                onClick={() => setExpanded(false)}
              >
                ×
              </button>
            </header>
            <div className="expanded-prompt-text">
              <PromptSourceBlocks
                prompt={preview.renderedPrompt}
                blocks={preview.promptBlocks}
              />
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <small>{label}</small>
      <strong>{value}</strong>
    </div>
  );
}

function qualityLabel(value: GenerationPreview["quality"]) {
  if (value === "draft") return "Черновик";
  if (value === "maximum") return "Максимальное";
  return "Стандартное";
}

function summarizeAssets(preview: GenerationPreview) {
  const labels: Record<string, string> = {
    logo: "логотип",
    product: "продукт",
    style: "стилевой референс",
    additional: "референс",
  };
  const summarize = (
    values: GenerationPreview["brandAssets"]["selected"],
    selection: string,
  ) => {
    const counts = new Map<string, number>();
    for (const value of values)
      counts.set(value.role, (counts.get(value.role) ?? 0) + 1);
    return [...counts].map(
      ([role, count]) =>
        `${count} ${labels[role] ?? "материал"} · ${selection}`,
    );
  };
  const values = [
    ...summarize(preview.brandAssets.selected, "вручную"),
    ...summarize(preview.brandAssets.automaticallySelected, "автоматически"),
  ];
  return values.length ? values.join("; ") : "Не используются";
}

function ratio(width: number, height: number) {
  const divisor = greatestCommonDivisor(width, height);
  return `${width / divisor}:${height / divisor}`;
}

function greatestCommonDivisor(left: number, right: number): number {
  return right === 0 ? left : greatestCommonDivisor(right, left % right);
}

function imageCountLabel(count: number) {
  if (count % 10 === 1 && count % 100 !== 11) return "изображение";
  if (
    count % 10 >= 2 &&
    count % 10 <= 4 &&
    (count % 100 < 12 || count % 100 > 14)
  ) {
    return "изображения";
  }
  return "изображений";
}
